import hljs from '@theme/code-highlight';

class LineItemGenerator extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('#generator-form');
    this.codeBlock = this.querySelector('#generated-code');
    this.preview = this.querySelector('#live-preview');
    this.optionsWrapper = this.querySelector('#field-options-wrapper');
    this.copyBtn = this.querySelector('#copy-btn');
  }

  connectedCallback() {
    if (!this.form) return;
    this.form.addEventListener('input', () => this.updateOutput());
    this.copyBtn?.addEventListener('click', () => this.copyCode());
    this.updateOutput();
  }

  // Convert label to Shopify-safe handle
  toHandle(str) {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9\s-]/g, '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-');
  }

  // Copy code to clipboard + status feedback
  async copyCode() {
    try {
      const code = this.codeBlock.textContent;
      await navigator.clipboard.writeText(code);

      this.copyBtn.textContent = 'Copied!';
      this.copyBtn.classList.add('is-copied');

      setTimeout(() => {
        this.copyBtn.textContent = 'Copy';
        this.copyBtn.classList.remove('is-copied');
      }, 1500);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  }

  updateOutput() {
    const type = this.form.querySelector('#field-type').value;
    const rawLabel = this.form.querySelector('#field-label').value.trim() || 'Field name';
    const label = rawLabel;
    const handle = this.toHandle(rawLabel);
    const options = this.form.querySelector('#field-options').value
      .split(',')
      .map(o => o.trim())
      .filter(Boolean);

    const required = this.form.querySelector('#field-required').checked ? 'required' : '';
    const hideCheckout = this.form.querySelector('#field-hide-checkout')?.checked;
    const attachForm = this.form.querySelector('#field-attach-form')?.checked;
    const formAttr = attachForm ? 'form="{{ product_form_id }}"' : '';
    const attr = [formAttr, required].filter(Boolean).join(' ');

    // Hide at checkout
    const propertyName = hideCheckout ? `_${label}` : label;
    const showOptions = ['select', 'radio', 'checkbox-group'].includes(type);
    this.optionsWrapper.style.display = showOptions ? '' : 'none';

    const id = `custom-${handle}`;
    let html = '';
    let previewHTML = '';

    switch (type) {
      case 'text':
        html = [
          `<div class="product-form__input field">`,
          `  <label for="${id}" class="field__label">${label}</label>`,
          `  <input id="${id}" type="text" name="properties[${propertyName}]" class="field__input"${attr ? ' ' + attr : ''}>`,
          `</div>`
        ].join('\n');
        previewHTML = html;
        break;

      case 'textarea':
        html = [
          `<div class="product-form__input field">`,
          `  <label for="${id}" class="field__label">${label}</label>`,
          `  <textarea id="${id}" name="properties[${propertyName}]" class="field__input"${attr ? ' ' + attr : ''}></textarea>`,
          `</div>`
        ].join('\n');
        previewHTML = html;
        break;

      case 'select':
        html = [
          `<div class="product-form__input field">`,
          `  <label for="${id}" class="field__label">${label}</label>`,
          `  <select id="${id}" name="properties[${propertyName}]" class="field__input"${attr ? ' ' + attr : ''}>`,
          `    <option value="">Please select</option>`,
          options.map(o => `    <option value="${o}">${o}</option>`).join('\n'),
          `  </select>`,
          `</div>`
        ].join('\n');
        previewHTML = html;
        break;

      case 'radio':
        html = [
          `<div class="product-form__input field">`,
          `  <span class="field__label">${label}</span>`,
          `  <div class="field-options">`,
          options
            .map(
              o => [
                `    <label class="field__label">`,
                `      <input type="radio" name="properties[${propertyName}]" value="${o}"${attr ? ' ' + attr : ''}>`,
                `      ${o}`,
                `    </label>`
              ].join('\n')
            )
            .join('\n'),
          `  </div>`,
          `</div>`
        ].join('\n');
        previewHTML = html;
        break;

      case 'checkbox':
        html = [
          `<div class="product-form__input field">`,
          `  <input type="hidden" name="properties[${propertyName}]" value="No"${formAttr ? ' ' + formAttr : ''}>`,
          `  <label for="${id}" class="field__label">`,
          `    <input id="${id}" type="checkbox" name="properties[${propertyName}]" value="Yes"${attr ? ' ' + attr : ''}>`,
          `    ${label}`,
          `  </label>`,
          `</div>`
        ].join('\n');
        previewHTML = [
          `<div class="product-form__input field">`,
          `  <label for="${id}" class="field__label">`,
          `    <input id="${id}" type="checkbox">`,
          `    ${label}`,
          `  </label>`,
          `</div>`
        ].join('\n');
        break;

      case 'checkbox-group':
        html = [
            `<div class="product-form__input field">`,
            `  <label class="field__label">${label}</label>`,
            `  <div class="field-options">`,
            options
            .map(
                (o, i) => [
                `    <label class="field__label">`,
                `      <input type="checkbox" id="${id}-${i}" data-group="${id}" onchange="updateGroupValue('${id}')" value="${o}"${formAttr ? ' ' + formAttr : ''}>`,
                `      ${o}`,
                `    </label>`
                ].join('\n')
            )
            .join('\n'),
            `  </div>`,
            `  <input type="hidden" id="${id}" name="properties[${propertyName}]"${formAttr ? ' ' + formAttr : ''}>`,
            `  <script>`,
            `    function updateGroupValue(groupId) {`,
            `      var checkboxes = document.querySelectorAll('[data-group=\"' + groupId + '\"]');`,
            `      var hiddenInput = document.getElementById(groupId);`,
            `      hiddenInput.value = "";`,
            `      checkboxes.forEach(cb => {`,
            `        if (cb.checked) hiddenInput.value += (hiddenInput.value ? ", " : "") + cb.value;`,
            `      });`,
            `    }`,
            `  </script>`,
            `</div>`
        ].join('\n');

        previewHTML = [
            `<div class="product-form__input field">`,
            `  <label class="field__label">${label}</label>`,
            `  <div class="field-options">`,
            options
            .map(
                o => [
                `    <label class="field__label">`,
                `      <input type="checkbox">`,
                `      ${o}`,
                `    </label>`
                ].join('\n')
            )
            .join('\n'),
            `  </div>`,
            `</div>`
        ].join('\n');
        break;

      case 'upload':
        html = [
          `<div class="product-form__input field">`,
          `  <label for="${id}" class="field__label">${label}</label>`,
          `  <input id="${id}" type="file" accept="image/*,.pdf,.eps,.psd,.ai,.sketch,.xd,.fig" name="properties[${propertyName}]" class="product-option__input"${attr ? ' ' + attr : ''}>`,
          `</div>`
        ].join('\n');
        previewHTML = [
          `<div class="product-form__input field">`,
          `  <label for="${id}" class="field__label">${label}</label>`,
          `  <input id="${id}" type="file" class="product-option__input">`,
          `</div>`
        ].join('\n');
        break;
    }

    // Update output + preview
    this.codeBlock.textContent = html;
    this.preview.innerHTML = previewHTML;

    // Refresh highlight
    this.codeBlock.removeAttribute('data-highlighted');
    requestAnimationFrame(() => {
      try {
        hljs.highlightElement(this.codeBlock);
      } catch (err) {
        console.warn('HighlightJS skipped:', err);
      }
    });
  }
}

// Global helper for checkbox-group
window.updateHiddenField = function (hiddenId) {
  const checkboxes = document.querySelectorAll(`[data-hidden="${hiddenId}"]`);
  const hiddenInput = document.getElementById(hiddenId);
  const values = [];
  checkboxes.forEach(cb => {
    if (cb.checked) values.push(cb.value);
  });
  hiddenInput.value = values.join(', ');
};

customElements.define('line-item-generator', LineItemGenerator);